import { Fragment, useState } from "react";
import { HistoryCloserDetailDTO } from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import {
  CloserMembership,
  groupByTeam,
  membershipOf,
  nameFromEmail,
  sumTotals,
} from "./closerAvailabilityHistoryStats.ts";
import {
  DeltaCell,
  SaturationCell,
  tdClass,
  thClass,
  thLeftClass,
  tdLeftClass,
} from "./HistoryCells.tsx";

type SortKey = "name" | "opening" | "closure" | "delta" | "calls" | "lost";

type Props = {
  closers: HistoryCloserDetailDTO[];
  teams: string[];
  hasClosure: boolean;
  comparable: boolean;
  showLost: boolean;
  onSelectCloser: (email: string) => void;
};

const MEMBERSHIP_BADGE: Record<CloserMembership, [string, string] | null> = {
  present: null,
  joined: ["entrato", "bg-blue-100 text-blue-700"],
  left: ["uscito", "bg-red-100 text-red-800"],
  left_during: ["uscito nel periodo", "bg-red-100 text-red-800"],
};

function lostOf(c: HistoryCloserDetailDTO): number {
  return (c.transitions?.blocked ?? 0) + (c.transitions?.callBlocked ?? 0);
}

function sortValue(c: HistoryCloserDetailDTO, key: SortKey): number | string {
  const opening = c.opening?.effective ?? 0;
  const closure = c.closure?.effective ?? 0;
  switch (key) {
    case "name":
      return nameFromEmail(c.email);
    case "opening":
      return opening;
    case "closure":
      return closure;
    case "delta":
      return closure - opening;
    case "calls":
      return c.closure?.inCall ?? 0;
    case "lost":
      return lostOf(c);
  }
}

export function HistoryCloserTable({
  closers,
  teams,
  hasClosure,
  comparable,
  showLost,
  onSelectCloser,
}: Props) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({
    key: "delta",
    dir: 1,
  });

  if (closers.length === 0) {
    return <p className="text-sm text-gray-400">Nessun dato disponibile.</p>;
  }

  const columns: [SortKey, string][] = [
    ["name", "Closer"],
    ["opening", "Capacity apertura"],
    ["closure", "Slot effettivi"],
    ["delta", "Δ"],
    ["calls", "Call"],
    ...(showLost ? [["lost", "Bloccati"] as [SortKey, string]] : []),
  ];

  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 1 ? -1 : 1 }
        : { key, dir: key === "name" || key === "delta" ? 1 : -1 },
    );

  const compare = (a: HistoryCloserDetailDTO, b: HistoryCloserDetailDTO) => {
    const x = sortValue(a, sort.key);
    const y = sortValue(b, sort.key);
    return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
  };

  const groups = groupByTeam(closers, teams);
  const colSpan = columns.length + 1;

  const totalRow = (
    label: string,
    rows: HistoryCloserDetailDTO[],
    className: string,
  ) => {
    const t = sumTotals(rows);
    const lost = rows.reduce((acc, c) => acc + lostOf(c), 0);
    return (
      <tr className={className}>
        <td className={tdLeftClass}>{label}</td>
        <td className={tdClass}>{t.opening}</td>
        <td className={tdClass}>{hasClosure ? t.closure : "—"}</td>
        <DeltaCell value={comparable ? t.closure - t.opening : null} />
        <td className={tdClass}>{t.calls}</td>
        {showLost && <td className={tdClass}>{lost}</td>}
        <SaturationCell calls={t.calls} effective={t.closure} />
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr>
            {columns.map(([key, label]) => (
              <th
                key={key}
                onClick={() => onSort(key)}
                className={`${key === "name" ? thLeftClass : thClass} cursor-pointer hover:text-gray-900`}
              >
                {label}
                {sort.key === key ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
              </th>
            ))}
            <th className={thClass}>Saturazione</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.team}>
              <tr>
                <td
                  colSpan={colSpan}
                  className="px-3 pt-5 pb-2 text-sm font-semibold text-gray-900 border-b border-gray-300"
                >
                  {group.team}{" "}
                  <span className="font-normal text-gray-400">
                    · {group.closers.length} closer
                  </span>
                </td>
              </tr>
              {[...group.closers].sort(compare).map((c) => {
                const badge = comparable
                  ? MEMBERSHIP_BADGE[membershipOf(c)]
                  : null;
                return (
                  <tr
                    key={c.email}
                    onClick={() => onSelectCloser(c.email)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className={tdLeftClass}>
                      <div className="flex flex-col">
                        <span>
                          {nameFromEmail(c.email)}
                          {badge && (
                            <span
                              className={`ml-2 text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${badge[1]}`}
                            >
                              {badge[0]}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-400">{c.email}</span>
                      </div>
                    </td>
                    <td className={tdClass}>{c.opening?.effective ?? "—"}</td>
                    <td className={tdClass}>
                      {hasClosure ? (c.closure?.effective ?? "—") : "—"}
                    </td>
                    <DeltaCell
                      value={
                        comparable
                          ? (c.closure?.effective ?? 0) -
                            (c.opening?.effective ?? 0)
                          : null
                      }
                    />
                    <td className={tdClass}>{c.closure?.inCall ?? "—"}</td>
                    {showLost && (
                      <td
                        className={`${tdClass} ${lostOf(c) ? "text-red-600" : "text-gray-300"}`}
                      >
                        {lostOf(c) || "—"}
                      </td>
                    )}
                    <SaturationCell
                      calls={c.closure?.inCall ?? 0}
                      effective={c.closure?.effective ?? 0}
                    />
                  </tr>
                );
              })}
              {totalRow(
                `Totale ${group.team}`,
                group.closers,
                "bg-slate-50 font-semibold",
              )}
            </Fragment>
          ))}
          {groups.length > 1 &&
            totalRow(
              "Totale periodo",
              closers,
              "bg-slate-100 font-semibold border-t-2 border-gray-300",
            )}
        </tbody>
      </table>
    </div>
  );
}
